/**
 * The language switcher, in the four shapes the product needs it:
 *
 *   `menu`      rows for a popover that is already open (the user menu)
 *   `dropdown`  a trigger showing only the current language, opening its own popover — the
 *               top bar, where nothing else is open yet
 *   `select`    a labelled control for Settings → Account
 *   `inline`    two words side by side, for the marketing footer and the sign-in card
 *
 * Each language is labelled in its *own* language — `Italiano`, `English`, never *Italian* or
 * *Inglese*. A person looking for their language recognises its own name and nothing else.
 *
 * Changing it applies immediately: the account row, the `pd.locale` mirror and the render all
 * move in one action (see `lib/i18n/locale.ts`). Signed out, the choice still survives a
 * reload; signed in, it survives a different browser.
 */

import { useRef, useState } from 'react'
import type { ReactElement } from 'react'

import { cn } from '../../lib/cn'
import { LOCALES, LOCALE_NATIVE_NAME, useLocale, useT } from '../../lib/i18n'
import type { Locale } from '../../lib/i18n'
import { Icon } from './Icon'
import type { ControlSize } from './Input'
import { MenuItem, MenuLabel, Popover } from './Popover'
import { Select } from './Select'

export type LocaleSwitcherVariant = 'menu' | 'dropdown' | 'select' | 'inline'

export interface LocaleSwitcherProps {
  variant?: LocaleSwitcherVariant
  surface?: 'paper' | 'chrome'
  /** `select` only. */
  size?: ControlSize
  /** `menu` only — the group heading above the rows. Off when the menu already says it. */
  showHeading?: boolean
  className?: string
  /** Fires after a language is picked — close the menu it lives in, for instance. */
  onChanged?: (locale: Locale) => void
}

/** The rows shared by `menu` and `dropdown` — a list, not a control, so it takes its own props. */
const LocaleRows = ({
  surface,
  locale,
  onPick,
}: {
  surface: 'paper' | 'chrome'
  locale: Locale
  onPick: (next: Locale) => void
}): ReactElement => (
  <>
    {LOCALES.map((option) => (
      <MenuItem
        key={option}
        surface={surface}
        active={option === locale}
        icon={
          option === locale ? (
            <Icon name="check" size={15} />
          ) : (
            <span className="inline-block w-3.75" aria-hidden="true" />
          )
        }
        onSelect={() => {
          onPick(option)
        }}
      >
        {LOCALE_NATIVE_NAME[option]}
      </MenuItem>
    ))}
  </>
)

export const LocaleSwitcher = ({
  variant = 'select',
  surface = 'paper',
  size = 'md',
  showHeading = true,
  className,
  onChanged,
}: LocaleSwitcherProps): ReactElement => {
  const t = useT()
  const { locale, setLocale, isSaving } = useLocale()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  const pick = (next: Locale): void => {
    if (next !== locale) setLocale(next)
    onChanged?.(next)
  }

  if (variant === 'dropdown') {
    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => {
            setOpen((previous) => !previous)
          }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t('common.locale.switcherLabel')}
          disabled={isSaving}
          className={cn(
            'ring-focus flex h-9 items-center gap-1.5 rounded-sm border px-2.5 transition-colors disabled:cursor-not-allowed disabled:opacity-60',
            surface === 'chrome'
              ? 'border-chrome-600 bg-chrome-800 text-chrome-mute hover:bg-chrome-700 hover:text-chrome-ink'
              : 'border-hair bg-surface text-ink-mute hover:text-ink',
            className,
          )}
        >
          <Icon name="globe" size={15} />
          <span className="t-small uppercase">{locale}</span>
          <Icon name="chevron-down" size={13} />
        </button>

        <Popover
          open={open}
          onClose={() => {
            setOpen(false)
          }}
          anchorRef={triggerRef}
          placement="bottom"
          align="end"
          surface={surface}
          role="menu"
          label={t('common.locale.switcherLabel')}
          className="py-1"
        >
          <LocaleRows
            surface={surface}
            locale={locale}
            onPick={(next) => {
              pick(next)
              setOpen(false)
            }}
          />
        </Popover>
      </>
    )
  }

  if (variant === 'menu') {
    return (
      <>
        {showHeading ? <MenuLabel surface={surface}>{t('common.locale.label')}</MenuLabel> : null}
        <LocaleRows surface={surface} locale={locale} onPick={pick} />
      </>
    )
  }

  if (variant === 'inline') {
    return (
      <div
        className={cn('flex items-center gap-1.5', className)}
        role="group"
        aria-label={t('common.locale.switcherLabel')}
      >
        <Icon
          name="globe"
          size={14}
          className={surface === 'chrome' ? 'text-chrome-mute' : 'text-ink-faint'}
          aria-hidden="true"
        />
        {LOCALES.map((option, index) => (
          <span key={option} className="flex items-center gap-1.5">
            {index > 0 ? (
              <span
                aria-hidden="true"
                className={surface === 'chrome' ? 'text-chrome-600' : 'text-ink-faint'}
              >
                ·
              </span>
            ) : null}
            <button
              type="button"
              lang={option}
              aria-current={option === locale ? 'true' : undefined}
              disabled={isSaving}
              onClick={() => {
                pick(option)
              }}
              className={cn(
                't-small ring-focus rounded-sm px-0.5 transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                option === locale
                  ? surface === 'chrome'
                    ? 'font-semibold text-chrome-ink'
                    : 'font-semibold text-ink'
                  : surface === 'chrome'
                    ? 'text-chrome-mute hover:text-chrome-ink'
                    : 'text-ink-mute hover:text-ink',
              )}
            >
              {LOCALE_NATIVE_NAME[option]}
            </button>
          </span>
        ))}
      </div>
    )
  }

  return (
    <Select
      aria-label={t('common.locale.switcherLabel')}
      value={locale}
      selectSize={size}
      tone={surface}
      disabled={isSaving}
      className={className}
      options={LOCALES.map((option) => ({ value: option, label: LOCALE_NATIVE_NAME[option] }))}
      onChange={(event) => {
        const next = event.target.value
        // The options are built from LOCALES, so this only narrows the DOM's `string`.
        const match = LOCALES.find((candidate) => candidate === next)
        if (match) pick(match)
      }}
    />
  )
}
